import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '../../../../../UserContext';
import { Plus, Trash2, AlertCircle, FilePenLine, Package, CheckCircle, Crosshair, ShoppingBag, Wrench, X, Calendar, PoundSterling, Hash, Filter } from 'lucide-react';
import EquipmentModal from './EquipmentModal';
import { equipmentService } from '../../../../../services';
import './Equipment.css';

const Equipment = () => {
    const [equipment, setEquipment] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [specifications, setSpecifications] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const { user } = useUser();

    const isFetchingCategories = useRef(false);
    const isFetchingEquipment = useRef(false);

    const fetchSpecifications = useCallback(async (categoryId) => {
        if (!categoryId) return;
        try {
            const data = await equipmentService.getSpecifications(categoryId);
            setSpecifications(data);
        } catch (err) {
            setError('Failed to load specifications');
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        if (categories.length > 0 || isFetchingCategories.current) return;
        isFetchingCategories.current = true;
        try {
            const data = await equipmentService.getCategories();
            setCategories(data);
        } catch (err) {
            setError('Failed to load equipment categories');
        } finally {
            isFetchingCategories.current = false;
        }
    }, [categories]);

    const fetchEquipment = useCallback(async () => {
        if (!user?.userId || isFetchingEquipment.current) return;
        isFetchingEquipment.current = true;
        try {
            const data = await equipmentService.getUserEquipment(user.userId);
            setEquipment(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Failed to load equipment');
        } finally {
            isFetchingEquipment.current = false;
        }
    }, [user?.userId]);

    useEffect(() => {
        if (!user?.userId) return;

        const loadData = async () => {
            if (!isFetchingCategories.current && !isFetchingEquipment.current) {
                setLoading(true);
                try {
                    await Promise.all([fetchCategories(), fetchEquipment()]);
                } catch (err) {
                    console.error('Error loading data:', err);
                } finally {
                    setLoading(false);
                }
            }
        };

        loadData();
    }, [fetchCategories, fetchEquipment, user]);

    const handleModalClose = useCallback(() => {
        setIsAddModalOpen(false);
        setIsEditModalOpen(false);
        setSelectedEquipment(null);
        setSpecifications([]);
        setBrands([]);
    }, []);

    const showSuccessMessage = useCallback((message) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(''), 3000);
    }, []);

    const showErrorMessage = useCallback((message) => {
        setError(message);
        setTimeout(() => setError(''), 3000);
    }, []);

    const handleCategoryChange = useCallback((categoryId) => {
        if (categoryId) {
            fetchSpecifications(categoryId);
        } else {
            setSpecifications([]);
        }
    }, [fetchSpecifications]);

    useEffect(() => {
        if (isEditModalOpen && selectedEquipment?.categoryId) {
            fetchSpecifications(selectedEquipment.categoryId);
        }
    }, [isEditModalOpen, selectedEquipment?.categoryId, fetchSpecifications]);

    const resolveBrandId = useCallback(async (formData) => {
        if (formData.brandId !== '__custom__') {
            return Number(formData.brandId);
        }

        const customBrandName = formData.customBrandName?.trim();
        if (!customBrandName) {
            throw new Error('Please add a brand name');
        }

        const brand = await equipmentService.createBrand({
            name: customBrandName,
            categoryId: Number(formData.categoryId)
        });

        return Number(brand.brandId);
    }, []);

    const buildEquipmentPayload = useCallback(async (formData) => {
        const resolvedBrandId = await resolveBrandId(formData);

        return {
            ...formData,
            categoryId: Number(formData.categoryId),
            brandId: resolvedBrandId,
            customBrandName: undefined,
            specifications: (formData.specifications || []).filter(spec =>
                spec?.definitionId && String(spec?.value || '').trim() !== ''
            )
        };
    }, [resolveBrandId]);

    const handleAddEquipment = async (formData) => {
        try {
            const payload = await buildEquipmentPayload(formData);
            await equipmentService.addEquipment(payload);
            await fetchEquipment();
            showSuccessMessage('Equipment added successfully');
            handleModalClose();
        } catch (err) {
            showErrorMessage(err.message || 'Failed to add equipment');
        }
    };

    const handleEditEquipment = async (formData) => {
        try {
            const payload = await buildEquipmentPayload(formData);
            await equipmentService.updateEquipment(selectedEquipment.equipmentId, {
                ...payload,
                specifications: payload.specifications.map(spec => ({
                    definitionId: spec.definitionId,
                    value: spec.value
                }))
            });
            await fetchEquipment();
            showSuccessMessage('Equipment updated successfully');
            setIsEditModalOpen(false);
        } catch (err) {
            showErrorMessage(err.message || 'Failed to update equipment');
        }
    };

    const handleDeleteEquipment = async (equipmentId) => {
        if (!window.confirm('Are you sure you want to delete this equipment?')) return;

        try {
            await equipmentService.deleteEquipment(equipmentId);
            await fetchEquipment();
            showSuccessMessage('Equipment deleted successfully');
        } catch (err) {
            showErrorMessage('Failed to delete equipment');
        }
    };

    // Get icon based on category
    const getCategoryIcon = (categoryName) => {
        const name = categoryName?.toLowerCase() || '';
        if (name.includes('gun') || name.includes('shotgun') || name.includes('rifle')) return Crosshair;
        if (name.includes('cartridge') || name.includes('ammo')) return ShoppingBag;
        return Wrench;
    };

    // Get condition badge color
    const getConditionColor = (condition) => {
        switch (condition) {
            case 'New': return 'condition-new';
            case 'Excellent': return 'condition-excellent';
            case 'Good': return 'condition-good';
            case 'Fair': return 'condition-fair';
            case 'Poor': return 'condition-poor';
            default: return '';
        }
    };

    const renderEquipmentCard = (item, category) => {
        const CategoryIcon = getCategoryIcon(category.name);
        const hasSpecs = item.specifications?.length > 0;
        
        return (
            <div key={item.equipmentId} className="equipment-card">
                {/* Card Header with Actions */}
                <div className="equipment-card-top">
                    <div className="equipment-card-icon-wrap">
                        <CategoryIcon size={22} />
                    </div>
                    <div className="equipment-card-actions-menu">
                        <button
                            className="equipment-action-btn edit"
                            onClick={() => {
                                setSelectedEquipment(item);
                                handleCategoryChange(item.categoryId);
                                setIsEditModalOpen(true);
                            }}
                            title="Edit"
                        >
                            <FilePenLine size={16} />
                        </button>
                        <button
                            className="equipment-action-btn delete"
                            onClick={() => handleDeleteEquipment(item.equipmentId)}
                            title="Delete"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                </div>

                {/* Card Body */}
                <div className="equipment-card-body">
                    <span className="equipment-card-category-tag">{category.name}</span>
                    <h3 className="equipment-card-title">{item.brandName}</h3>
                    {category.showModel && item.model && (
                        <p className="equipment-card-model">{item.model}</p>
                    )}

                    {/* Condition Badge */}
                    {item.condition && (
                        <span className={`equipment-condition-badge ${getConditionColor(item.condition)}`}>
                            {item.condition}
                        </span>
                    )}
                </div>

                {/* Specs Grid */}
                {(hasSpecs || item.serialNumber || item.purchaseDate || item.purchasePrice) && (
                    <div className="equipment-specs-grid">
                        {hasSpecs && item.specifications.map(spec => (
                            <div key={spec.specificationId} className="equipment-spec-item">
                                <span className="equipment-spec-label">{spec.name}</span>
                                <span className="equipment-spec-value">{spec.value}{spec.unit ? ` ${spec.unit}` : ''}</span>
                            </div>
                        ))}
                        
                        {category.showSerialNumber && item.serialNumber && (
                            <div className="equipment-spec-item">
                                <span className="equipment-spec-label">
                                    <Hash size={12} /> Serial
                                </span>
                                <span className="equipment-spec-value">{item.serialNumber}</span>
                            </div>
                        )}
                        
                        {item.purchaseDate && (
                            <div className="equipment-spec-item">
                                <span className="equipment-spec-label">
                                    <Calendar size={12} /> Purchased
                                </span>
                                <span className="equipment-spec-value">
                                    {new Date(item.purchaseDate).toLocaleDateString('en-GB', { 
                                        day: 'numeric', 
                                        month: 'short', 
                                        year: 'numeric' 
                                    })}
                                </span>
                            </div>
                        )}
                        
                        {item.purchasePrice && (
                            <div className="equipment-spec-item highlight">
                                <span className="equipment-spec-label">
                                    <PoundSterling size={12} /> Price
                                </span>
                                <span className="equipment-spec-value price">
                                    £{item.purchasePrice.toFixed(2)}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderCategories = useCallback(() => {
        const equipmentMap = new Map(
            equipment.map(item => [item.categoryId, true])
        );

        return categories
            .filter(c => c.isActive)
            .sort((a, b) => {
                const aHasEquipment = equipmentMap.has(a.categoryId);
                const bHasEquipment = equipmentMap.has(b.categoryId);

                if (aHasEquipment !== bHasEquipment) {
                    return aHasEquipment ? -1 : 1;
                }
                if (a.categoryId === 1) return -1;
                if (b.categoryId === 1) return 1;

                return a.name.localeCompare(b.name);
            });
    }, [categories, equipment]);

    if (loading) {
        return (
            <div className="equipment-page">
                <div className="equipment-loading">
                    <div className="equipment-loading-spinner"></div>
                    <p>Loading equipment...</p>
                </div>
            </div>
        );
    }

    // Calculate total value
    const totalValue = equipment.reduce((sum, item) => sum + (item.purchasePrice || 0), 0);

    // Get categories that have equipment
    const categoriesWithEquipment = renderCategories().filter(c => 
        equipment.some(e => e.categoryId === c.categoryId)
    );

    // Filter equipment based on active filter
    const filteredEquipment = activeFilter === 'all' 
        ? equipment 
        : equipment.filter(item => item.categoryId === parseInt(activeFilter));

    return (
        <div className="equipment-page dashboard-component">
            {/* Header */}
            <div className="equipment-header">
                <div className="equipment-header-content">
                    <div className="equipment-header-icon">
                        <Package size={28} />
                    </div>
                    <div className="equipment-header-text">
                        <h1>Equipment</h1>
                        <p>Track your guns, cartridges, and gear to analyze performance</p>
                    </div>
                </div>
                <button className="equipment-add-btn" onClick={() => setIsAddModalOpen(true)}>
                    <Plus size={20} />
                    <span>Add Equipment</span>
                </button>
            </div>

            {/* Error Toast */}
            {error && (
                <div className="equipment-toast equipment-toast-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                    <button onClick={() => setError('')} className="toast-close"><X size={16} /></button>
                </div>
            )}

            {/* Success Toast */}
            {successMessage && (
                <div className="equipment-toast equipment-toast-success">
                    <CheckCircle size={18} />
                    <span>{successMessage}</span>
                    <button onClick={() => setSuccessMessage('')} className="toast-close"><X size={16} /></button>
                </div>
            )}

            {/* Content */}
            {equipment.length === 0 ? (
                <div className="equipment-empty">
                    <div className="equipment-empty-icon">
                        <Crosshair size={48} />
                    </div>
                    <h3>No Equipment Added Yet</h3>
                    <p>Start tracking your shooting gear to analyze which equipment gives you the best performance.</p>
                    <button className="equipment-empty-btn" onClick={() => setIsAddModalOpen(true)}>
                        <Plus size={18} />
                        Add Your First Equipment
                    </button>
                </div>
            ) : (
                <>
                    {/* Stats Bar */}
                    <div className="equipment-stats-bar">
                        <div className="equipment-stats-left">
                            <div className="equipment-stat-pill">
                                <Package size={16} />
                                <span>{equipment.length} Items</span>
                            </div>
                            {totalValue > 0 && (
                                <div className="equipment-stat-pill highlight">
                                    <PoundSterling size={16} />
                                    <span>£{totalValue.toLocaleString()} Total Value</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Category Filter Tabs */}
                    <div className="equipment-filter-tabs">
                        <button 
                            className={`equipment-filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('all')}
                        >
                            <Filter size={16} />
                            All Equipment
                            <span className="tab-count">{equipment.length}</span>
                        </button>
                        {categoriesWithEquipment.map(category => {
                            const CategoryIcon = getCategoryIcon(category.name);
                            const count = equipment.filter(e => e.categoryId === category.categoryId).length;
                            return (
                                <button 
                                    key={category.categoryId}
                                    className={`equipment-filter-tab ${activeFilter === String(category.categoryId) ? 'active' : ''}`}
                                    onClick={() => setActiveFilter(String(category.categoryId))}
                                >
                                    <CategoryIcon size={16} />
                                    {category.name}
                                    <span className="tab-count">{count}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Equipment Grid */}
                    <div className="equipment-grid-container">
                        <div className="equipment-grid">
                            {filteredEquipment.map(item => {
                                const category = categories.find(c => c.categoryId === item.categoryId);
                                if (!category) return null;
                                return renderEquipmentCard(item, category);
                            })}
                        </div>
                    </div>
                </>
            )}

            {/* Modals */}
            {isAddModalOpen && (
                <EquipmentModal
                    categories={categories}
                    brands={brands}
                    onClose={handleModalClose}
                    onSubmit={handleAddEquipment}
                    onCategoryChange={handleCategoryChange}
                    specifications={specifications}
                />
            )}

            {isEditModalOpen && selectedEquipment && (
                <EquipmentModal
                    categories={categories}
                    brands={brands}
                    onClose={handleModalClose}
                    onSubmit={handleEditEquipment}
                    onCategoryChange={handleCategoryChange}
                    specifications={specifications}
                    equipment={selectedEquipment}
                    isEdit
                />
            )}
        </div>
    );
};

export default Equipment;
